import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Button } from '@goaltree/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@goaltree/ui/components/dialog'
import { Input } from '@goaltree/ui/components/input'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { ArrowLeft, Check, ChevronRight, Repeat } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { coachErrorCode, toastCoachError } from '@/components/coaching/coach-utils'
import { goalUrl } from '@/lib/utils'

export type PlanSource =
  | { kind: 'goal'; goalId: Id<'nodes'> }
  | { kind: 'template'; templateId: Id<'templates'> }
  | { kind: 'private'; templateId: Id<'templates'> }
type Source = PlanSource
type Tab = 'mine' | 'goals' | 'library'

const PLAN_ERRORS = ['clientGoalLimit', 'clientHabitLimit', 'pickedFull', 'notFound', 'empty']
const PREVIEW_TASKS = 4

const chip = (active: boolean) =>
  cn(
    'cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition-colors',
    active
      ? 'border-primary/40 bg-primary/10 text-primary-text'
      : 'text-muted-foreground hover:text-foreground',
  )

export function GivePlanDialog({
  clientId,
  clientName,
  goals,
  open,
  onOpenChange,
  initialSource = null,
}: {
  clientId: string
  clientName: string
  goals: { _id: Id<'nodes'>; title: string; done: boolean }[]
  open: boolean
  onOpenChange: (open: boolean) => void
  // Skips the picker: the plan was already chosen (from the Library).
  initialSource?: Source | null
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('mine')
  const [search, setSearch] = useState('')
  const [source, setSource] = useState<Source | null>(initialSource)
  const [into, setInto] = useState<Id<'nodes'> | null>(null)
  const [busy, setBusy] = useState(false)
  const mine = useQuery(api.coachPlans.myPlans, open ? {} : 'skip')
  const library = useQuery(
    api.templates.list,
    open && tab === 'library' ? { locale: i18n.language } : 'skip',
  )
  const preview = useQuery(api.coachPlans.preview, source ? { clientId, source } : 'skip')
  const give = useMutation(api.coachPlans.give)

  const q = search.trim().toLowerCase()
  const rows =
    tab === 'mine'
      ? mine?.templates.map((tpl) => ({
          key: tpl._id,
          title: tpl.title,
          sub: t('coachPlans.taskCount', { count: tpl.taskCount }),
          source: { kind: 'private', templateId: tpl._id } as Source,
        }))
      : tab === 'goals'
        ? mine?.goals.map((g) => ({
            key: g._id,
            title: g.title,
            sub: g.category,
            source: { kind: 'goal', goalId: g._id } as Source,
          }))
        : library?.map((tpl) => ({
            key: tpl.id,
            title: tpl.title,
            sub: t('coachPlans.taskCount', { count: tpl.taskCount }),
            source: { kind: 'template', templateId: tpl.id } as Source,
          }))
  const shown = rows?.filter((r) => !q || r.title.toLowerCase().includes(q))
  const openGoals = goals.filter((g) => !g.done)

  const reset = () => {
    setSource(initialSource)
    setInto(null)
    setSearch('')
  }

  const confirm = async () => {
    if (!source) return
    setBusy(true)
    try {
      const res = await give({ clientId, source, ...(into ? { intoGoalId: into } : {}) })
      onOpenChange(false)
      reset()
      toast.success(t('coachPlans.given', { name: clientName, title: res.goalTitle }), {
        action: {
          label: t('coachPlans.open'),
          onClick: () =>
            void navigate({
              to: '/goals/$goalId',
              params: { goalId: goalUrl(res.goalTitle, res.goalId).replace('/goals/', '') },
            }),
        },
      })
    } catch (err) {
      const code = coachErrorCode(err)
      if (code && PLAN_ERRORS.includes(code))
        toast.error(t(`coachPlans.errors.${code}`, { name: clientName }))
      else toastCoachError(err, t)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="flex max-h-[85dvh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>{t('coachPlans.title', { name: clientName })}</DialogTitle>
          <DialogDescription>{t('coachPlans.desc')}</DialogDescription>
        </DialogHeader>

        {!source ? (
          <>
            <div className="flex gap-1.5">
              {(['mine', 'goals', 'library'] as const).map((k) => (
                <button key={k} type="button" onClick={() => setTab(k)} className={chip(tab === k)}>
                  {t(`coachPlans.${k}`)}
                </button>
              ))}
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('coachPlans.search')}
              className="mt-3"
            />
            <div className="mt-3 min-h-40 flex-1 overflow-y-auto rounded-2xl border">
              {shown === undefined ? (
                <div className="h-40 animate-pulse bg-muted/40" />
              ) : shown.length === 0 ? (
                <p className="px-4 py-4 text-sm text-muted-foreground">
                  {q
                    ? t('coachPlans.noResults')
                    : tab === 'mine'
                      ? t('coachPlans.noMine')
                      : tab === 'goals'
                        ? t('coachPlans.noGoals')
                        : t('coachPlans.noResults')}
                </p>
              ) : (
                shown.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setSource(r.source)}
                    className="flex w-full cursor-pointer items-center gap-3 border-t px-4 py-3 text-left transition-colors first:border-t-0 hover:bg-muted/40"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{r.title}</p>
                      <p className="truncate text-xs text-muted-foreground capitalize">{r.sub}</p>
                    </div>
                    <ChevronRight className="size-4 text-muted-foreground" />
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setSource(null)}
              className="inline-flex w-fit cursor-pointer items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              {t('coachPlans.back')}
            </button>
            <div className="mt-3 min-h-32 flex-1 overflow-y-auto rounded-2xl border bg-card px-4 py-3">
              {preview === undefined ? (
                <div className="h-32 animate-pulse rounded-xl bg-muted/40" />
              ) : preview === null ? (
                <p className="text-sm text-muted-foreground">{t('coachPlans.errors.notFound')}</p>
              ) : (
                <>
                  <p className="font-semibold">{preview.title}</p>
                  <ol className="mt-2 space-y-2">
                    {preview.milestones.map((m, i) => (
                      <li key={i}>
                        <p className="text-sm font-medium">{m.title}</p>
                        <ul className="mt-0.5 space-y-0.5 pl-3 text-xs text-muted-foreground">
                          {m.tasks.slice(0, PREVIEW_TASKS).map((task, j) => (
                            <li key={j} className="truncate">
                              · {task}
                            </li>
                          ))}
                          {m.tasks.length > PREVIEW_TASKS && (
                            <li>
                              {t('coachPlans.more', { count: m.tasks.length - PREVIEW_TASKS })}
                            </li>
                          )}
                        </ul>
                      </li>
                    ))}
                  </ol>
                  {preview.habits.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-muted-foreground">
                        {t('coachPlans.habits')}
                      </p>
                      <ul className="mt-0.5 space-y-0.5 text-xs">
                        {preview.habits.map((h, i) => (
                          <li key={i} className="flex items-center gap-1.5 truncate">
                            <Repeat className="size-3 text-muted-foreground" />
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            <p className="mt-4 text-xs font-semibold text-muted-foreground">
              {t('coachPlans.where')}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {[
                { _id: null, label: t('coachPlans.newGoal') },
                ...openGoals.map((g) => ({
                  _id: g._id,
                  label: t('coachPlans.addTo', { title: g.title }),
                })),
              ].map((o) => (
                <button
                  key={o._id ?? 'new'}
                  type="button"
                  onClick={() => setInto(o._id)}
                  className={cn(chip(into === o._id), 'inline-flex max-w-full items-center gap-1')}
                >
                  {into === o._id && <Check className="size-3 shrink-0" />}
                  <span className="truncate">{o.label}</span>
                </button>
              ))}
            </div>
            <Button className="mt-5" disabled={busy || !preview} onClick={() => void confirm()}>
              {t('coachPlans.confirm')}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
